import { validateAndFormat } from "../../common/addNew/validateAndFormat";
import { validateAndFormatBeforeEdit } from "../../common/validateAndFormatBeforeEdit";
import { ERROR_MESSSAGE } from "../../config";

/**
 * Actions for clients
 */
export const actions = {
  /**
   * Set values for edit client
   *
   * @param {Object} context
   * @param {Number} id
   */
  editClient(context, id) {
    // Step 0: Getting the selected provider id
    const selectedProviderId = context.rootGetters.getSelectedProviderID;

    // Step 1: Getting all the clients
    const clients = context.getters.getClients;
    if (!(selectedProviderId in clients)) {
      return false;
    }

    // Step 2: Getting the client
    const client = clients[selectedProviderId].find(curClient => {
      return curClient.id === id;
    });

    // Setting pha_state
    const stateCodes = context.rootGetters["addNew/getStateCodes"];
    let stateCodeId = 0;

    for (let i = 0; i < stateCodes.length; i++) {
      let code = stateCodes[i];
      if (code.state.toLowerCase() === client.pha_state.toLowerCase()) {
        stateCodeId = code.id;
        break;
      }
    }

    // Step 3: Create the request data
    const reqData = {
      timezone: client.timezone,
      firstName: client.firstName,
      lastName: client.lastName,
      pha_line1: client.pha_line1,
      pha_line2: client.pha_line2,
      pha_city: client.pha_city,
      pha_state: stateCodeId,
      pha_postalCode: client.pha_postalCode,
      notes: client.notes === null ? "" : client.notes,
      phone: [
        client.phone1,
        client.phone2,
        client.phone3,
        client.phone4
      ].filter(phone => {
        return phone !== "" && phone !== null;
      }),
      email: [client.email1, client.email2].filter(email => {
        return email !== null && email !== "";
      }),
      phoneType: [client.pht1, client.pht2, client.pht3, client.pht4].map(
        pht => {
          switch (pht) {
            case "C":
              return 1;

            case "H":
              return 2;

            case "W":
              return 3;

            case "F":
              return 4;

            case "O":
              return 5;

            default:
              return 0;
          }
        }
      ),
      birthDate:
        client.birthDate === null
          ? {
              dayOfMonth: 0,
              month: 0,
              year: 0
            }
          : validateAndFormatBeforeEdit.date(client.birthDate)
    };
    context.commit("addNew/setEdit", reqData, {
      root: true
    });
    context.commit("setMode", "EDIT");
    context.commit("setEditClientId", client.id);
  },

  /**
   * Get client details
   *
   * @param {Object} context
   */
  async getClientDetails(context) {
    // Getting the selected provider id
    const selectedProviderId = context.rootGetters.getSelectedProviderID;
    const dataLoaded = context.getters.getDataLoaded;

    // Only fetch the data for the provider if data has not already been fetched
    if (
      !(selectedProviderId in dataLoaded) ||
      dataLoaded[selectedProviderId] !== true
    ) {
      // Setting loading to true
      context.commit("setLoading", true);

      const json = await context.dispatch(
        "makeAuthRequest",
        {
          url: `clients/client_details/${selectedProviderId}`,
          method: "GET"
        },
        { root: true }
      );

      // Checking if data has been sent
      if (
        "clients" in json &&
        json.clients !== false &&
        typeof json.clients === "object"
      ) {
        // Setting the clients data
        context.commit("setClients", {
          providerId: selectedProviderId,
          value: json.clients
        });

        // Setting that the data has been loaded
        context.commit("setDataLoaded", {
          providerId: selectedProviderId,
          value: true
        });
      }

      // Setting loading to false
      context.commit("setLoading", false);
    }
  },

  /**
   * Send add client request
   *
   * @param {Object} context
   */
  async addClient(context, addOrEdit) {
    // Start loading
    context.commit("addNew/startLoading", null, { root: true });
    context.commit("addNew/setFormSubmitted", false, { root: true }); // Form not submitted

    // Step 0 Getting the selected provider id
    const selectedProviderId = context.rootGetters.getSelectedProviderID;

    // Step 1: Validate and format everything
    const name = context.rootGetters["addNew/getName"];
    const firstName = validateAndFormat.name(
      name.firstName,
      context,
      "firstName"
    );
    const lastName = validateAndFormat.name(name.lastName, context, "lastName");
    const address = context.rootGetters["addNew/getAddress"];
    const pha_line1 = validateAndFormat.name(
      address.pha_line1,
      context,
      "address"
    );
    const pha_line2 = validateAndFormat.name(
      address.pha_line2,
      context,
      "address"
    );
    const pha_city = validateAndFormat.name(
      address.pha_city,
      context,
      "address"
    );
    const pha_state = validateAndFormat.stateCode(address.pha_state, context);
    const pha_postalCode = validateAndFormat.postalCode(
      address.pha_postalCode,
      context
    );

    // Validate and format phone
    let phone = context.rootGetters["addNew/getPhone"]
      .map(curPhone => {
        return validateAndFormat.phone(curPhone, context);
      })
      .filter(curPhone => {
        return curPhone !== "";
      });
    for (let i = 0; i < phone.length; i++) {
      if (!phone[i]) {
        phone = false;
        break;
      }
    }
    const notes = context.rootGetters["addNew/getNotes"].trim();

    // Get the phone types
    const selectedPhoneTypes = context.rootGetters["addNew/getPhoneType"];

    // Get all the phone types
    const allPhoneTypes = context.rootGetters["addNew/getAllPhoneTypes"];

    // Validate and format email
    let email = context.rootGetters["addNew/getEmail"].map(curEmail => {
      return validateAndFormat.email(curEmail, context);
    });
    for (let i = 0; i < email.length; i++) {
      if (!email[i]) {
        email = false;
        break;
      }
    }

    const date = validateAndFormat.date(
      context.rootGetters["addNew/getBirthDate"],
      context
    );
    const timezone = validateAndFormat.name(
      context.rootGetters["addNew/getSelectedTimezone"],
      context,
      "timezone"
    );
    const selectedInsuranceCompany = validateAndFormat.insuranceCompany(
      context.rootGetters["addNew/getSelectedInsuranceValue"],
      context
    );
    const insuranceId = validateAndFormat.insuranceId(
      context.rootGetters["addNew/getInsuranceId"],
      selectedInsuranceCompany,
      context
    );
    const insuranceType = context.rootGetters["addNew/getInsuranceType"];

    // Step 2: Check if everything is valid
    if (
      !(
        firstName &&
        lastName &&
        pha_state !== false &&
        pha_postalCode !== false &&
        phone !== false &&
        email !== false &&
        timezone !== false &&
        date !== false &&
        selectedInsuranceCompany !== false &&
        (selectedInsuranceCompany === 0 ||
          (selectedInsuranceCompany && insuranceId))
      )
    ) {
      // Stop loading
      context.commit("addNew/stopLoading", null, {
        root: true
      });
      return false;
    }

    // Step 3: Creating request data
    let requestData = {
      firstName: firstName,
      lastName: lastName,
      phone: phone,
      phoneType: selectedPhoneTypes,
      allPhoneTypes: allPhoneTypes,
      email: email,
      date: date,
      timezone: timezone,
      providerID: selectedProviderId,
      pha_line1: pha_line1,
      pha_line2: pha_line2,
      pha_city: pha_city,
      pha_state: pha_state,
      pha_postalCode: pha_postalCode,
      notes: notes
    };

    if (selectedInsuranceCompany !== 0) {
      requestData.insuranceCompany = selectedInsuranceCompany;
      requestData.insuranceId = insuranceId;
      requestData.insuranceType = insuranceType;
    } else {
      requestData.insuranceCompany = false;
      requestData.insuranceId = false;
      requestData.insuranceType = false;
    }

    if (addOrEdit === "EDIT") {
      // Step 4: Get the client id that is to be edited
      requestData.clientId = context.getters.getEditClientId;

      // Step 5: Send auth request
      const json = await context.dispatch(
        "makeAuthRequest",
        {
          url: `clients/edit/${selectedProviderId}`,
          method: "PUT",
          data: requestData
        },
        {
          root: true
        }
      );

      if (
        !("client" in json) ||
        !(selectedProviderId in context.getters.getClients)
      ) {
        // Stop loading
        context.commit("addNew/stopLoading", null, {
          root: true
        });

        // Show error message if any
        if (json.code === 400) {
          context.dispatch("globalMessagePopup", json.message, {
            root: true
          });
        }

        // Return
        return false;
      }

      const clientIndex = context.getters.getClients[
        selectedProviderId
      ].findIndex(client => {
        return client.id === requestData.clientId;
      });

      if (clientIndex === -1) {
        // Stop loading
        context.commit("addNew/stopLoading", null, {
          root: true
        });
        return false;
      }

      const newClient = [...context.getters.getClients[selectedProviderId]];
      newClient[clientIndex] = {
        ...newClient[clientIndex],
        firstName: json.client.firstName,
        lastName: json.client.lastName,
        pha_line1: json.client.pha_line1,
        pha_line2: json.client.pha_line2,
        pha_city: json.client.pha_city,
        pha_state: json.client.pha_state,
        pha_postalCode: json.client.pha_postalCode,
        email1: json.client.email1,
        email2: json.client.email2,
        phone1: json.client.phone1,
        phone2: json.client.phone2,
        phone3: json.client.phone3,
        phone4: json.client.phone4,
        pht1: json.client.pht1,
        pht2: json.client.pht2,
        pht3: json.client.pht3,
        pht4: json.client.pht4,
        birthDate: json.client.birthDate,
        timezone: json.client.timezone,
        notes: json.client.notes
      };

      // Update clients
      context.commit("updateNewClient", {
        providerId: selectedProviderId,
        client: newClient
      });

      // Update form details
      context.commit(
        "updateNewClient",
        {
          selectedProviderId: selectedProviderId,
          clientId: requestData.clientId,
          value: {
            name: json.client.lastName + ", " + json.client.firstName,
            phone: [
              json.client.phone1,
              json.client.phone2,
              json.client.phone3,
              json.client.phone4
            ].filter(currPhone => {
              return currPhone !== null;
            })
          }
        },
        { root: true }
      );

      // Update chat
      if (newClient[clientIndex].userID !== null) {
        context.commit(
          "chat/updateNewClient",
          {
            value: {
              firstName: json.client.firstName,
              lastName: json.client.lastName
            },
            clientId: requestData.clientId
          },
          { root: true }
        );
      }
    } else {
      // Step 4: Send auth request
      const json = await context.dispatch(
        "makeAuthRequest",
        {
          url: "clients/add",
          method: "POST",
          data: requestData
        },
        {
          root: true
        }
      );

      // Insert into clients and form details if required
      if ("client" in json) {
        const client = {
          email1: json.client.email1,
          email2: json.client.email2,
          firstName: json.client.firstName,
          id: json.client.id,
          lastName: json.client.lastName,
          middleName: "",
          namePrefix: "",
          nameSuffix: "",
          phone1: json.client.phone1,
          phone2: json.client.phone2,
          phone3: json.client.phone3,
          phone4: json.client.phone4,
          pht1: json.client.pht1,
          pht2: json.client.pht2,
          pht3: json.client.pht3,
          pht4: json.client.pht4,
          providerID: selectedProviderId,
          birthDate: json.client.birthDate,
          timezone: json.client.timezone,
          status: 1,
          userID: null,
          favourite_id: null,
          pha_line1: json.client.pha_line1,
          pha_line2: json.client.pha_line2,
          pha_city: json.client.pha_city,
          pha_state: json.client.pha_state,
          pha_postalCode: json.client.pha_postalCode,
          notes: json.client.notes
        };

        const clientForm = {
          id: json.client.id,
          favourite_id: 0,
          firstName: json.client.firstName,
          lastName: json.client.lastName,
          provider: selectedProviderId,
          phone: [
            json.client.phone1,
            json.client.phone2,
            json.client.phone3,
            json.client.phone4
          ],
          type: "person",
          user_id: 0
        };

        // Add to clients list
        context.commit("setClients", {
          providerId: selectedProviderId,
          value: [client]
        });

        // Add to form list
        context.commit(
          "addToClientListInForm",
          {
            client: clientForm,
            providerId: selectedProviderId
          },
          {
            root: true
          }
        );
      } else if (
        "message" in json &&
        json.message === "client added successfully"
      ) {
        context.dispatch("globalMessagePopup", ERROR_MESSSAGE.clientAdded, {
          root: true
        });
      } else if (json.code !== 200) {
        context.dispatch("globalMessagePopup", json.message, {
          root: true
        });
        context.commit("addNew/stopLoading", null, { root: true });
        return false;
      }
    }

    // Stop loading
    context.commit("addNew/setFormSubmitted", true, { root: true }); // Form submitted
    context.commit("addNew/stopLoading", null, { root: true });
  },

  /**
   * Add phone number to a client
   *
   * @param {Object} context
   * @param {Object} param1
   */
  async addPhone(context, { phone, clientId, by }) {
    // Step 1: Getting the selected provider id
    const selectedProviderId = context.rootGetters.getSelectedProviderID;

    // Step 2: Sanitizing phone number
    const formattedPhone = validateAndFormat.phone(phone, context);

    // Step 3: Sanitizing client id
    const formattedClientId = validateAndFormat.simpleInt(clientId);

    // Step 4: If everything is not valid, then exit
    if (!selectedProviderId || !formattedPhone || !formattedClientId) {
      return;
    }

    // Step 5: Make request based on if client or others phone is being added
    if (by === "CLIENTS") {
      // Step 7: Make auth request
      const json = await context.dispatch(
        "makeAuthRequest",
        {
          url: `clients/add_phone/${selectedProviderId}`,
          method: "PUT",
          data: {
            phone: formattedPhone,
            clientId: formattedClientId
          }
        },
        { root: true }
      );

      // Add phone to the state
      if ("phone" in json && "number" in json) {
        context.commit("appendPhone", {
          ...json,
          selectedProviderId,
          clientId: formattedClientId
        });

        context.commit(
          "appendPhone",
          {
            ...json,
            selectedProviderId,
            clientId: formattedClientId
          },
          { root: true }
        );
      }
    } else {
      // Step 6: Make auth request
      const json = await context.dispatch(
        "makeAuthRequest",
        {
          url: "others/add_phone",
          method: "PUT",
          data: {
            phone: formattedPhone,
            otherEventId: formattedClientId
          }
        },
        { root: true }
      );

      if ("phone" in json && "number" in json) {
        context.commit(
          "appendPhoneOthers",
          {
            ...json,
            selectedProviderId,
            otherEventId: formattedClientId
          },
          { root: true }
        );
      }
    }
  }
};
